import { bucketIndex, HISTOGRAM_EDGES } from "./measures";

/**
 * Visits (ADR 0016; docs/admin-app/figures.md › Visits): one stretch of a user's requests, ending
 * after 30 minutes without one. A visit lives only in the installation's memory while it lasts and
 * leaves only as bucketed day counts (`visitCounts`).
 */

/** A visit ends after this long without a request. */
export const VISIT_GAP_MINUTES = 30;
/** Gaps between requests shorter than this count as active time; longer ones as away. */
export const ACTIVE_GAP_MINUTES = 5;

/** Lower edges in seconds of the duration and active time buckets: under 1 min, 1–5, 5–15, 15–30, 30–60 min, 1–2 h, over 2 h. */
export const VISIT_DURATION_EDGES_SECONDS = [0, 60, 300, 900, 1800, 3600, 7200] as const;

/** Reads are tRPC queries (looking around, approximately); changes are mutations (exact). */
export type RequestKind = "read" | "change";

export interface Visit {
  /** Epoch milliseconds of the first and the last request. */
  start: number;
  last: number;
  activeSeconds: number;
  reads: number;
  changes: number;
  /** Areas (`PROCEDURE_AREAS`) read and changed, each once, in the order first seen. */
  areasRead: string[];
  areasChanged: string[];
  /** Interactions the browser reported during it, for those who share usage statistics. */
  interactions?: number;
}

export interface VisitRequest {
  at: number;
  kind: RequestKind;
  /** The procedure's area, when it has one. */
  area?: string | null;
}

const MINUTE = 60_000;

/** Whether a visit is over at `now`: its last request lies `VISIT_GAP_MINUTES` back. */
export function visitOver(visit: Visit, now: number): boolean {
  return now - visit.last >= VISIT_GAP_MINUTES * MINUTE;
}

function startVisit({ at, kind, area }: VisitRequest): Visit {
  return {
    start: at,
    last: at,
    activeSeconds: 0,
    reads: kind === "read" ? 1 : 0,
    changes: kind === "change" ? 1 : 0,
    areasRead: kind === "read" && area ? [area] : [],
    areasChanged: kind === "change" && area ? [area] : [],
  };
}

/**
 * One request into the user's current visit: it continues the visit, or ends it and starts the
 * next after `VISIT_GAP_MINUTES` without a request. `ended` is the visit that just ended, if any.
 */
export function visitStep(
  visit: Visit | null,
  request: VisitRequest,
): { visit: Visit; ended: Visit | null } {
  if (visit === null || visitOver(visit, request.at)) {
    return { visit: startVisit(request), ended: visit };
  }
  const gap = Math.max(0, request.at - visit.last);
  const { kind, area } = request;
  const add = (areas: string[]) => (area && !areas.includes(area) ? [...areas, area] : areas);
  return {
    visit: {
      ...visit,
      last: Math.max(visit.last, request.at),
      activeSeconds: visit.activeSeconds + (gap < ACTIVE_GAP_MINUTES * MINUTE ? gap / 1000 : 0),
      reads: visit.reads + (kind === "read" ? 1 : 0),
      changes: visit.changes + (kind === "change" ? 1 : 0),
      areasRead: kind === "read" ? add(visit.areasRead) : visit.areasRead,
      areasChanged: kind === "change" ? add(visit.areasChanged) : visit.areasChanged,
    },
    ended: null,
  };
}

/**
 * The day counts a visit leaves (`usage_counts`), per device type. Bucketed metrics have the
 * value `<device>:<lower edge>`: seconds for duration and active time (`VISIT_DURATION_EDGES_SECONDS`),
 * a count for reads and changes (`HISTOGRAM_EDGES`). `visit.start` is the UTC hour of the week,
 * 0 for Monday 00:00. The `visit.sum.*` counts make exact means possible.
 */
export const VISIT_METRICS = [
  "visits",
  "visit.duration",
  "visit.active",
  "visit.reads",
  "visit.changes",
  "visit.with_change",
  "visit.start",
  "visit.sum.duration",
  "visit.sum.active",
  "visit.sum.reads",
  "visit.sum.changes",
  "visit.interactions",
  "visit.sum.interactions",
] as const;
export type VisitMetric = (typeof VISIT_METRICS)[number];

/** The UTC hour of the week, 0 (Monday 00:00) to 167. */
export function hourOfWeek(at: number): number {
  const date = new Date(at);
  return ((date.getUTCDay() + 6) % 7) * 24 + date.getUTCHours();
}

export function visitCounts(visit: Visit, device: string): [VisitMetric, string, number][] {
  const duration = Math.round((visit.last - visit.start) / 1000);
  const active = Math.round(visit.activeSeconds);
  const bucket = (value: number, edges: readonly number[]) =>
    `${device}:${edges[bucketIndex(value, edges)] ?? 0}`;
  const rows: [VisitMetric, string, number][] = [
    ["visits", device, 1],
    ["visit.duration", bucket(duration, VISIT_DURATION_EDGES_SECONDS), 1],
    ["visit.active", bucket(active, VISIT_DURATION_EDGES_SECONDS), 1],
    ["visit.reads", bucket(visit.reads, HISTOGRAM_EDGES), 1],
    ["visit.changes", bucket(visit.changes, HISTOGRAM_EDGES), 1],
    ["visit.with_change", device, visit.changes > 0 ? 1 : 0],
    ["visit.start", String(hourOfWeek(visit.start)), 1],
    ["visit.sum.duration", device, duration],
    ["visit.sum.active", device, active],
    ["visit.sum.reads", device, visit.reads],
    ["visit.sum.changes", device, visit.changes],
  ];
  // Only for someone who shares usage statistics: the others' visits report none.
  const interactions = visit.interactions ?? 0;
  if (interactions > 0) {
    rows.push(
      ["visit.interactions", bucket(interactions, HISTOGRAM_EDGES), 1],
      ["visit.sum.interactions", device, interactions],
    );
  }
  return rows.filter(([, , count]) => count > 0);
}

/** One person's day, counted at the UTC rollover; the value is the bucket's lower edge (`HISTOGRAM_EDGES`). */
export const PERSON_DAY_METRICS = [
  "person.visits",
  "person.active_minutes",
  "person.changes",
] as const;
export type PersonDayMetric = (typeof PERSON_DAY_METRICS)[number];

export function personDayCounts(day: {
  visits: number;
  activeSeconds: number;
  changes: number;
}): [PersonDayMetric, string, number][] {
  const bucket = (value: number) =>
    String(HISTOGRAM_EDGES[bucketIndex(value, HISTOGRAM_EDGES)] ?? 0);
  return [
    ["person.visits", bucket(day.visits), 1],
    ["person.active_minutes", bucket(Math.round(day.activeSeconds / 60)), 1],
    ["person.changes", bucket(day.changes), 1],
  ];
}

/** Lower edges in days of the sign-in session age buckets: under a day, 1–7, 7–14, 14–30, 30 and more. */
export const SESSION_AGE_EDGES_DAYS = [0, 1, 7, 14, 30] as const;

/** The live sign-in sessions (a cookie, not a visit): how many, per user, and how old. */
export interface SessionStats {
  live: number;
  /** Sessions of each user who has one. */
  perUser: number[];
  /** Counts per bucket of `SESSION_AGE_EDGES_DAYS`, by when each session started. */
  ageBuckets: number[];
}

/** The figures of the sessions still live at `now`; each is its owner and its start and end. */
export function sessionStats(
  sessions: readonly { owner: string; createdAt: number; expiresAt: number }[],
  now: number,
): SessionStats {
  const live = sessions.filter((s) => s.expiresAt > now);
  const perUser = new Map<string, number>();
  const ageBuckets = SESSION_AGE_EDGES_DAYS.map(() => 0);
  for (const session of live) {
    perUser.set(session.owner, (perUser.get(session.owner) ?? 0) + 1);
    const days = Math.max(0, now - session.createdAt) / (24 * 60 * MINUTE);
    const bucket = bucketIndex(days, SESSION_AGE_EDGES_DAYS);
    ageBuckets[bucket] = (ageBuckets[bucket] ?? 0) + 1;
  }
  return { live: live.length, perUser: [...perUser.values()], ageBuckets };
}
